import { Printer, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePrintBill } from '@/hooks/api/useFolio';
import { BillPreviewModal } from './BillPreviewModal';

interface PrintBillButtonProps {
  bookingId: string;
  variant?: 'button' | 'icon' | 'inline';
  label?: string;
  className?: string;
}

/**
 * Generates a guest invoice and shows the PDF in a blurred overlay modal.
 * Drop this anywhere a booking ID is available — it manages its own state.
 */
export function PrintBillButton({
  bookingId,
  variant = 'button',
  label = 'Print Bill',
  className,
}: PrintBillButtonProps) {
  const { openBill, isPrinting, error, preview, closePreview } = usePrintBill();

  const handleClick = () => void openBill(bookingId);

  const icon = isPrinting
    ? <Loader2 size={variant === 'button' ? 16 : 14} className="animate-spin" aria-hidden="true" />
    : <Printer size={variant === 'button' ? 16 : 14} aria-hidden="true" />;

  const displayLabel = isPrinting ? 'Generating...' : error ? 'Retry' : label;

  return (
    <>
      {/* ── Trigger button ────────────────────────────────────────────────── */}

      {variant === 'icon' && (
        <button
          type="button"
          onClick={handleClick}
          disabled={isPrinting}
          aria-label={label}
          title={error ? `Error: ${error}` : label}
          className={cn(
            'p-1.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            isPrinting ? 'text-gray-300 cursor-not-allowed'
              : error ? 'text-red-400 hover:text-red-600 hover:bg-red-50'
                : 'text-gray-400 hover:text-primary-600 hover:bg-primary-50',
            className,
          )}
        >
          {icon}
        </button>
      )}

      {variant === 'inline' && (
        <button
          type="button"
          onClick={handleClick}
          disabled={isPrinting}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            isPrinting ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : error ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : 'bg-violet-50 text-violet-700 hover:bg-violet-100',
            className,
          )}
        >
          {icon}
          {displayLabel}
        </button>
      )}

      {variant === 'button' && (
        <button
          type="button"
          onClick={handleClick}
          disabled={isPrinting}
          className={cn(
            'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            isPrinting ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : error ? 'bg-red-50 border border-red-200 text-red-600 hover:bg-red-100'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
            className,
          )}
        >
          {icon}
          {displayLabel}
        </button>
      )}

      {/* ── Preview modal (renders into document body via portal-like pattern) */}
      {preview && (
        <BillPreviewModal preview={preview} onClose={closePreview} />
      )}
    </>
  );
}
